import { truncate } from 'lodash';

import { COURSE_PROGRAM_CHAR_LIMIT } from 'bundles/notification-center/constants';
import type { EnrolledProgramWithCourseNames as EnrolledProgramWithCourseNamesType } from 'bundles/notification-center/types';

/**
 * Formats a list of course objects into a readable string with truncation.
 * For 4 or fewer courses, joins them with commas and "and" before the last item.
 * For more than 4 courses, shows first 2 courses and indicates remaining count.
 *
 * @param {Array<{name: string; slug: string; type: string}>} courses - Array of course objects to format
 * @returns {string} Formatted string of course names
 *
 * @example
 * // With 3 courses
 * formatCourseList([
 *   { name: 'Math 101', slug: 'math-101', type: 'core' },
 *   { name: 'Physics 201', slug: 'physics-201', type: 'elective' },
 *   { name: 'Chemistry 301', slug: 'chemistry-301', type: 'core' }
 * ])
 * // Returns: 'Math 101, Physics 201 and Chemistry 301'
 *
 * @example
 * // With 5 courses
 * formatCourseList([
 *   { name: 'Math 101', slug: 'math-101', type: 'core' },
 *   { name: 'Physics 201', slug: 'physics-201', type: 'elective' },
 *   { name: 'Chemistry 301', slug: 'chemistry-301', type: 'core' },
 *   { name: 'Biology 401', slug: 'biology-401', type: 'core' },
 *   { name: 'English 501', slug: 'english-501', type: 'elective' }
 * ])
 * // Returns: 'Math 101, Physics 201 and 3 other courses'
 *
 * @remarks
 * - Course names are truncated to COURSE_PROGRAM_CHAR_LIMIT characters
 * - For lists of 4 or fewer, the last item's name is truncated to 50 characters
 * - Only the course name field is used in the formatted output
 */
export default function formatCourseList(courses: EnrolledProgramWithCourseNamesType['data']['courses']) {
  if (courses.length <= 4) {
    const lastCourse = truncate(courses[courses.length - 1].name, { length: COURSE_PROGRAM_CHAR_LIMIT });
    const otherCourses = courses
      .slice(0, -1)
      .map((course) => truncate(course.name, { length: COURSE_PROGRAM_CHAR_LIMIT }))
      .join(', ');
    return courses.length === 1
      ? truncate(courses[0].name, { length: COURSE_PROGRAM_CHAR_LIMIT })
      : `${otherCourses} and ${lastCourse}`;
  }
  const firstTwo = courses
    .slice(0, 2)
    .map((course) => truncate(course.name, { length: COURSE_PROGRAM_CHAR_LIMIT }))
    .join(', ');
  const remainingCount = courses.length - 2;
  return `${firstTwo} and ${remainingCount} other courses`;
}
